import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Save, ArrowLeft, Image as ImageIcon, X } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";

const RARITIES = ["rare", "epic", "legendary"];

function defaultEndsAt() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
}

export default function AdminRaffleForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    sport: "Football",
    image_url: "",
    card_value: 100,
    price_per_ticket: 1.99,
    currency: "gbp",
    total_tickets: 500,
    max_tickets_per_user: 50,
    rarity: "rare",
    question: "",
    options: ["", "", "", ""],
    correct_answer_index: 0,
    ends_at: defaultEndsAt(),
    status: "live",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/competitions/${id}`);
        setForm({
          ...data,
          ends_at: new Date(data.ends_at).toISOString().slice(0, 16),
          options: data.options.length ? data.options : ["", "", "", ""],
        });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateOption = (i, v) => setForm((f) => {
    const opts = [...f.options];
    opts[i] = v;
    return { ...f, options: opts };
  });
  const addOption = () => setForm((f) => (f.options.length < 6 ? { ...f, options: [...f.options, ""] } : f));
  const removeOption = (i) => setForm((f) => {
    if (f.options.length <= 2) return f;
    const opts = f.options.filter((_, idx) => idx !== i);
    const correct = f.correct_answer_index >= opts.length ? 0 : f.correct_answer_index;
    return { ...f, options: opts, correct_answer_index: correct };
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      update("image_url", fullUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanedOptions = form.options.map((o) => o.trim()).filter(Boolean);
    if (cleanedOptions.length < 2) {
      toast.error("At least 2 answer options required");
      return;
    }
    if (form.correct_answer_index >= cleanedOptions.length) {
      toast.error("Correct answer index out of range");
      return;
    }
    if (!form.question.trim()) {
      toast.error("Trivia question is required");
      return;
    }
    const payload = {
      ...form,
      options: cleanedOptions,
      card_value: parseFloat(form.card_value),
      price_per_ticket: parseFloat(form.price_per_ticket),
      total_tickets: parseInt(form.total_tickets, 10),
      max_tickets_per_user: parseInt(form.max_tickets_per_user, 10),
      correct_answer_index: parseInt(form.correct_answer_index, 10),
      ends_at: new Date(form.ends_at).toISOString(),
    };
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/admin/competitions/${id}`, payload);
        toast.success("Raffle updated");
      } else {
        await api.post("/admin/competitions", payload);
        toast.success("Raffle created");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-raffle-form">
      <Link to="/admin" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back
      </Link>
      <h1 className="font-display text-4xl text-white mb-6">{isEdit ? "Edit raffle" : "New raffle"}</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image upload */}
        <div className="ufr-card rounded-md p-5">
          <Label>Card photo</Label>
          <div className="grid sm:grid-cols-[180px_1fr] gap-4 items-start mt-2">
            <div className="aspect-[3/4] bg-black rounded-md border border-white/10 overflow-hidden relative">
              {form.image_url ? (
                <>
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" data-testid="admin-image-preview" />
                  <button type="button" onClick={() => update("image_url", "")} className="absolute top-2 right-2 bg-black/80 border border-white/20 p-1 hover:border-red-400">
                    <X size={12} className="text-white" />
                  </button>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/30">
                  <ImageIcon size={36} />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="admin-image-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ufr-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="admin-image-upload-btn">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
              </button>
              <div className="text-[11px] text-white/50">JPG, PNG, WEBP up to 10MB.</div>
              <div className="text-[11px] text-white/40 pt-3">or paste an image URL:</div>
              <input className="ufr-input text-sm" placeholder="https://..." value={form.image_url} onChange={(e) => update("image_url", e.target.value)} data-testid="admin-image-url-input" />
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="ufr-card rounded-md p-5 grid sm:grid-cols-2 gap-4">
          <Field label="Title (player + card)" required>
            <input className="ufr-input" required value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="admin-field-title" placeholder="e.g. Erling Haaland Topps Chrome Auto" />
          </Field>
          <Field label="Subtitle (team / set)">
            <input className="ufr-input" value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} data-testid="admin-field-subtitle" placeholder="Man City · 2023 Topps Chrome RC" />
          </Field>
          <Field label="Card value (£)" required>
            <input type="number" min="0" step="0.01" className="ufr-input" required value={form.card_value} onChange={(e) => update("card_value", e.target.value)} data-testid="admin-field-card-value" />
          </Field>
          <Field label="Price per ticket (£)" required>
            <input type="number" min="0" step="0.01" className="ufr-input" required value={form.price_per_ticket} onChange={(e) => update("price_per_ticket", e.target.value)} data-testid="admin-field-price" />
          </Field>
          <Field label="Total tickets" required>
            <input type="number" min="1" step="1" className="ufr-input" required value={form.total_tickets} onChange={(e) => update("total_tickets", e.target.value)} data-testid="admin-field-total-tickets" />
          </Field>
          <Field label="Max tickets per customer" required>
            <input type="number" min="1" step="1" className="ufr-input" required value={form.max_tickets_per_user} onChange={(e) => update("max_tickets_per_user", e.target.value)} data-testid="admin-field-max-tickets" />
          </Field>
          <Field label="Rarity">
            <select className="ufr-input" value={form.rarity} onChange={(e) => update("rarity", e.target.value)} data-testid="admin-field-rarity">
              {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Ends at" required>
            <input type="datetime-local" required className="ufr-input" value={form.ends_at} onChange={(e) => update("ends_at", e.target.value)} data-testid="admin-field-ends-at" />
          </Field>
          <Field label="Status">
            <select className="ufr-input" value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="admin-field-status">
              <option value="live">live</option>
              <option value="draft">draft</option>
              <option value="ended">ended</option>
            </select>
          </Field>
        </div>

        {/* Description */}
        <div className="ufr-card rounded-md p-5">
          <Label>Card description</Label>
          <div className="mt-2">
            <RichTextEditor
              value={form.description || ""}
              onChange={(html) => update("description", html)}
              placeholder="Describe the card — condition, grading, serial number, why it's special…"
              testId="admin-field-description"
            />
          </div>
          <div className="text-[11px] text-white/50 mt-1">Use the toolbar for bold, sizes and bullet points. Shown on the raffle page. Optional.</div>
        </div>

        {/* Trivia */}
        <div className="ufr-card rounded-md p-5">
          <Label>Skill question</Label>
          <input className="ufr-input mt-2" required value={form.question} onChange={(e) => update("question", e.target.value)} placeholder="e.g. Which club did Haaland sign for in 2022?" data-testid="admin-field-question" />

          <div className="mt-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">Options (pick the correct one)</div>
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <button type="button" onClick={() => update("correct_answer_index", i)} className={`w-8 h-8 shrink-0 border text-xs font-bold flex items-center justify-center ${
                  form.correct_answer_index === i
                    ? "border-[color:var(--ufr-green)] bg-[rgba(34,255,102,0.15)] text-[color:var(--ufr-green)]"
                    : "border-white/15 text-white/60"
                }`} data-testid={`admin-field-option-correct-${i}`}>
                  {String.fromCharCode(65 + i)}
                </button>
                <input className="ufr-input" value={opt} onChange={(e) => updateOption(i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid={`admin-field-option-${i}`} />
                {form.options.length > 2 && (
                  <button type="button" onClick={() => removeOption(i)} className="btn-ufr-ghost px-2 py-2 text-[11px]"><X size={12} /></button>
                )}
              </div>
            ))}
            {form.options.length < 6 && (
              <button type="button" onClick={addOption} className="btn-ufr-ghost px-4 py-2 text-[11px]" data-testid="admin-add-option-btn">+ Add option</button>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Link to="/admin" className="btn-ufr-ghost px-6 py-3 text-sm">Cancel</Link>
          <button type="submit" disabled={loading} className="btn-ufr-primary px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="admin-save-btn">
            <Save size={14} /> {loading ? "Saving…" : isEdit ? "Save changes" : "Create raffle"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold mb-1.5">
        {label}{required && <span className="text-[color:var(--ufr-green)] ml-1">*</span>}
      </div>
      {children}
    </div>
  );
}
